import React, { useEffect, useState } from 'react';
import { useReactFlow, useViewport } from '@xyflow/react';
import { useWorkflowStore } from '@/lib/workflows/store';

import { SimpleTooltip } from '@/components/ui/tooltip';

export function ZoomControls() {
  const { zoomIn, zoomOut, fitView, getNodes, setCenter } = useReactFlow();
  const { zoom } = useViewport();
  const selectedNode = useWorkflowStore((state) => state.selectedNode);

  // Format zoom as percentage
  const zoomPercentage = Math.round(zoom * 100);

  // Zoom to selected node
  const handleZoomToSelection = () => {
    if (!selectedNode) return;

    const nodes = getNodes();
    const node = nodes.find((n) => n.id === selectedNode);

    if (node) {
      // Center on the node with zoom level 1
      setCenter(
        node.position.x + (node.width || 0) / 2,
        node.position.y + (node.height || 0) / 2,
        { zoom: 1, duration: 400 },
      );
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMod = event.metaKey || event.ctrlKey;

      // Zoom in: Cmd/Ctrl + =
      if (isMod && (event.key === '=' || event.key === '+')) {
        event.preventDefault();
        zoomIn({ duration: 200 });
      }

      // Zoom out: Cmd/Ctrl + -
      if (isMod && event.key === '-') {
        event.preventDefault();
        zoomOut({ duration: 200 });
      }

      // Zoom to fit: Cmd/Ctrl + 0
      if (isMod && event.key === '0') {
        event.preventDefault();
        fitView({ duration: 400, padding: 0.2 });
      }

      // Zoom to selection: Cmd/Ctrl + 1
      if (isMod && event.key === '1' && selectedNode) {
        event.preventDefault();
        handleZoomToSelection();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [zoomIn, zoomOut, fitView, selectedNode]);

  return (
    <div className='absolute left-4 bottom-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden z-10'>
      {/* Zoom percentage display */}
      <div className='px-3 py-2 border-b border-gray-200 dark:border-gray-700 text-center'>
        <span className='text-xs font-semibold text-gray-700 dark:text-gray-300'>
          {zoomPercentage}%
        </span>
      </div>

      {/* Zoom controls */}
      <div className='flex flex-col'>
        {/* Zoom In */}
        <SimpleTooltip title='Zoom In (Cmd/Ctrl + =)' side='right'>
          <button
            onClick={() => zoomIn({ duration: 200 })}
            className='p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700 flex items-center justify-center'
          >
            <svg
              className='w-5 h-5 text-gray-700 dark:text-gray-300'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7' />
            </svg>
          </button>
        </SimpleTooltip>

        {/* Zoom Out */}
        <SimpleTooltip title='Zoom Out (Cmd/Ctrl + -)' side='right'>
          <button
            onClick={() => zoomOut({ duration: 200 })}
            className='p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700 flex items-center justify-center'
          >
            <svg
              className='w-5 h-5 text-gray-700 dark:text-gray-300'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7' />
            </svg>
          </button>
        </SimpleTooltip>

        {/* Zoom to Fit */}
        <SimpleTooltip title='Zoom to Fit (Cmd/Ctrl + 0)' side='right'>
          <button
            onClick={() => fitView({ duration: 400, padding: 0.2 })}
            className='p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700 flex items-center justify-center'
          >
            <svg
              className='w-5 h-5 text-gray-700 dark:text-gray-300'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path d='M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4' />
            </svg>
          </button>
        </SimpleTooltip>

        {/* Zoom to Selection */}
        <SimpleTooltip
          title={
            selectedNode
              ? 'Zoom to Selection (Cmd/Ctrl + 1)'
              : 'Zoom to Selection (select a node first)'
          }
          side='right'
        >
          <button
            onClick={handleZoomToSelection}
            disabled={!selectedNode}
            className='p-2 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center'
          >
            <svg
              className='w-5 h-5 text-gray-700 dark:text-gray-300'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              viewBox='0 0 24 24'
              stroke='currentColor'
            >
              <path d='M15 12a3 3 0 11-6 0 3 3 0 016 0z' />
              <path d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' />
            </svg>
          </button>
        </SimpleTooltip>
      </div>
    </div>
  );
}
